import {Injectable} from "@angular/core";
import {BehaviorSubject, Observable} from "rxjs";
import {NavItem} from "libComm";
import {cloneDeep} from "lodash";
import {HttpClient} from "@angular/common/http";
import {environment} from "../../environments/environment";

interface ServerResponse
{
	iResponseCode: number;
	oContent: any;
	sResponseMessage: string;
}

@Injectable({
	providedIn: "root"
})
export class MenuService
{
	// BehaviorSubject to manage the state of the menu
	private readonly _menu = new BehaviorSubject<NavItem[]>([]);

	// Observable for menu state to be used in components
	readonly menu$: Observable<NavItem[]> = this._menu.asObservable();

	constructor(private readonly httpClient: HttpClient)
	{
	}

	/**
	 * Getter for the current menu state.
	 * @returns NavItem[] - Current menu items.
	 */
	get menu(): NavItem[]
	{
		return this._menu.value;
	}

	/**
	 * Updates the menu state with new values.
	 * @param newValue - New menu items to update the state.
	 */
	updateState(newValue: NavItem[]): void
	{
		this._menu.next(cloneDeep(newValue));
	}

	/**
	 * Resets the menu state to an empty array.
	 */
	resetState(): void
	{
		this._menu.next([]);
	}

	/**
	 * Updates a specific menu item identified by `pageId` with the provided updates.
	 * @param pageId - ID of the page to update.
	 * @param updates - Partial updates to be applied to the menu item.
	 * @returns boolean - Returns true if the update is successful, false otherwise.
	 */
	updateMenuItem(pageId: string, updates: Partial<NavItem>): boolean
	{
		const isUpdated = this.updateMenuRecursive(pageId, updates, this.menu);

		if (isUpdated)
		{
			this._menu.next(cloneDeep(this.menu)); // Trigger state update
		}

		return isUpdated;
	}

	/**
	 * Recursively updates a menu item identified by `pageId` in a given menu list.
	 * @param pageId - ID of the page to update.
	 * @param updates - Partial updates to be applied.
	 * @param menuList - List of menu items to search in (default is the current menu state).
	 * @returns boolean - Returns true if the update is successful, false otherwise.
	 */
	private updateMenuRecursive(pageId: string,
								updates: Partial<NavItem>,
								menuList: NavItem[] = this.menu
	): boolean
	{
		for (const item of menuList)
		{
			// Match the menu item by pageId
			if (item.pageId === pageId)
			{
				Object.assign(item, updates);
				return true;
			}

			// If the item has children, search recursively
			if (item.children?.length)
			{
				const found = this.updateMenuRecursive(pageId, updates, item.children);
				if (found)
				{
					return true;
				}
			}
		}

		return false;
	}

	/**
	 * Fetches badge information from the server and updates the menu items accordingly.
	 */
	updateBadgeInfo(): void
	{
		if (this.menu.length === 0)
		{
			console.error("There is no menu to update");
			return;
		}

		const url = `${environment.apiUrl}badge-menu`;

		this.httpClient.get<ServerResponse>(url).subscribe({
			next: (response: ServerResponse) =>
			{
				if (response.oContent)
				{
					Object.entries(response.oContent).forEach(([key, value]) =>
					{
						this.updateMenuItem(key, {badge: value as number});
					});
				}
			},
			error: (error) =>
			{
				console.error("Error fetching badge information:", error);
			}
		});
	}
}
